import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  AlertTriangle, CheckCircle, ArrowRight, FileText, Download, 
  MessageSquare, Shield, Target, Zap, Clock, ChevronRight,
  AlertCircle, ClipboardList, Users, Building2, Sparkles
} from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';
import LegalFooter from '../components/LegalFooter';
import { Button } from '../components/ui/button';
import { trackEvent } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configurations
const STATUS_CONFIG = {
  high_risk: {
    label: 'High Risk',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: AlertTriangle,
    description: 'Your organization has significant AI accountability gaps that may create exposure.'
  },
  partial_documentation: {
    label: 'Developing',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    icon: Clock,
    description: 'You have some governance practices in place, but key gaps remain.'
  },
  structured_governance: {
    label: 'Structured',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    icon: CheckCircle,
    description: 'You have baseline governance practices. Consider formalizing your commitment.'
  }
};

// Product configurations
const PRODUCT_CONFIG = {
  documentation_system: {
    name: 'AI Accountability Documentation System',
    price: '$97',
    description: 'Complete documentation templates to address your governance gaps',
    cta: 'Get the Documentation System',
    link: null, // Will be checkout
    features: [
      'AI Acceptable Use Policy template',
      'Tool inventory spreadsheet',
      'Incident logging framework',
      'Employee acknowledgment form',
      'Vendor assessment checklist',
      'Governance summary template'
    ]
  },
  assessment: {
    name: 'AI Accountability Assessment',
    price: '$297',
    description: 'Comprehensive assessment to identify all accountability gaps',
    cta: 'Get the Full Assessment',
    link: '/assessment-product',
    features: [
      'Detailed gap analysis',
      'Prioritized action plan',
      'Framework alignment review',
      'Risk classification',
      'Remediation roadmap'
    ]
  },
  declaration: {
    name: 'AASC Public Declaration',
    price: '$997/year',
    description: 'Public accountability declaration with verification',
    cta: 'Start Your Declaration',
    link: '/apply',
    features: [
      'Public registry listing',
      'Verification badge',
      'Annual renewal support',
      'Framework alignment'
    ]
  }
};

export default function AssessmentResults() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [actionPlan, setActionPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [loadError, setLoadError] = useState('');  // Fatal error - assessment not found
  const [checkoutError, setCheckoutError] = useState('');  // Recoverable error - checkout failed
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const fetchAssessment = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/assessment/${assessmentId}`);
      if (!response.ok) {
        throw new Error('Assessment not found');
      }
      const data = await response.json();
      setAssessment(data);
      
      // Track action plan view
      trackEvent('action_plan_viewed', {
        assessment_id: assessmentId,
        score: data.score,
        status: data.status
      });
      
      // Check if action plan exists
      if (data.action_plan) {
        setActionPlan(data.action_plan);
      } else {
        // Generate action plan
        generateActionPlan();
      }
    } catch (err) {
      setLoadError('Failed to load assessment results');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  const generateActionPlan = async () => {
    setGeneratingPlan(true);
    try {
      const response = await fetch(`${API_URL}/api/assessment/${assessmentId}/generate-action-plan`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const plan = await response.json();
        setActionPlan(plan);
        
        // Track generation
        trackEvent('action_plan_generated', {
          assessment_id: assessmentId
        });
      } else if (response.status === 429) {
        // Handle rate limiting gracefully
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail || {};
        const message = detail.message || 'Please wait a moment before trying again.';
        console.warn('Action plan rate limited:', message);
        // Don't show error UI - user can retry later, and action plan may already exist
      }
    } catch (err) {
      console.error('Failed to generate action plan:', err);
    } finally {
      setGeneratingPlan(false);
    }
  };

  useEffect(() => {
    if (assessmentId) {
      fetchAssessment();
    }
  }, [assessmentId, fetchAssessment]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');  // Clear any previous checkout error
    
    // Track checkout start
    trackEvent('documentation_checkout_started', {
      assessment_id: assessmentId,
      user_email: assessment?.email,
      product_name: 'documentation_system',
      value_cents: 9700
    });
    
    try {
      const response = await fetch(`${API_URL}/api/assessment/${assessmentId}/create-checkout`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        setCheckoutError('Unable to start checkout. Please try again or contact support.');
        setCheckoutLoading(false);
      }
    } catch (err) {
      setCheckoutError('Unable to connect to payment system. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const openAxiomWithContext = () => {
    // Store assessment context for Axiom
    if (assessment && actionPlan) {
      const axiomContext = {
        type: 'assessment_result',
        assessment_id: assessmentId,
        score: assessment.score,
        status: assessment.status,
        business_name: assessment.business_name,
        biggest_gap: actionPlan.biggest_gap?.area,
        findings: actionPlan.findings?.map(f => f.area),
        priority_actions: actionPlan.priority_actions?.map(a => a.action)
      };
      sessionStorage.setItem('axiom_context', JSON.stringify(axiomContext));
      
      // Track Axiom open with context
      trackEvent('axiom_with_context', {
        assessment_id: assessmentId,
        context_type: 'assessment_result'
      });
    }
    
    // Trigger Axiom to open (dispatch custom event)
    window.dispatchEvent(new CustomEvent('openAxiom', { 
      detail: { 
        initialMessage: "I just completed my AI Accountability Assessment. Can you help me understand my results and what I should do next?" 
      } 
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <MainHeader />
        <main className="py-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loadError || !assessment) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <MainHeader />
        <main className="py-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#102A43] mb-4">
              {loadError || 'Assessment not found'}
            </h1>
            <Button onClick={() => navigate('/assessment/form')}>
              Take the Assessment
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[assessment.status] || STATUS_CONFIG.high_risk;
  const StatusIcon = statusConfig.icon;
  const recommendedProduct = actionPlan?.recommended_product?.product 
    ? PRODUCT_CONFIG[actionPlan.recommended_product.product]
    : PRODUCT_CONFIG.documentation_system;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Score Header */}
          <div className={`${statusConfig.bgColor} ${statusConfig.borderColor} border rounded-[12px] p-8 mb-8`}>
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="text-center md:text-left">
                <p className="text-sm uppercase tracking-wide text-[#64748B] mb-2">Your AI Accountability Score</p>
                <div className="flex items-baseline gap-3" data-testid="assessment-score">
                  <span className={`text-6xl font-bold ${statusConfig.color}`} data-testid="score-value">{assessment.score}</span>
                  <span className="text-2xl text-[#64748B]">/ 100</span>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <StatusIcon className={`w-5 h-5 ${statusConfig.color}`} />
                  <span className={`text-lg font-semibold ${statusConfig.color}`}>{statusConfig.label}</span>
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <p className="text-[#334E68] text-lg">
                  {actionPlan?.overall_assessment?.summary || statusConfig.description}
                </p>
              </div>
            </div>
          </div>

          {/* Generating Plan Indicator */}
          {generatingPlan && !actionPlan && (
            <div className="bg-white border border-[#E2E8F0] rounded-[12px] p-8 mb-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#2F80ED] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-[#64748B]">Generating your personalized action plan...</p>
            </div>
          )}

          {/* Action Plan Content */}
          {actionPlan && (
            <>
              {/* Biggest Gap */}
              {actionPlan.biggest_gap && (
                <div className="bg-white border border-[#E2E8F0] rounded-[12px] p-8 mb-8" data-testid="biggest-gap-card">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm uppercase tracking-wide text-red-600 font-semibold mb-1">Your Biggest Accountability Gap</p>
                      <h2 className="text-2xl font-bold text-[#102A43] mb-3" data-testid="biggest-gap-area">{actionPlan.biggest_gap.area}</h2>
                      <p className="text-[#64748B] mb-3">{actionPlan.biggest_gap.why_it_matters}</p>
                      <p className="text-sm text-[#94A3B8]">
                        <strong>Potential Impact:</strong> {actionPlan.biggest_gap.impact}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Findings */}
              {actionPlan.findings && actionPlan.findings.length > 0 && (
                <div className="bg-white border border-[#E2E8F0] rounded-[12px] p-8 mb-8" data-testid="findings-card">
                  <h2 className="text-xl font-bold text-[#102A43] mb-6 flex items-center gap-3">
                    <ClipboardList className="w-6 h-6 text-[#2F80ED]" />
                    What We Found
                  </h2>
                  <div className="space-y-4">
                    {actionPlan.findings.slice(0, 6).map((finding, index) => (
                      <div 
                        key={index}
                        data-testid={`finding-${index}`}
                        data-status={finding.status}
                        className={`flex items-start gap-4 p-4 rounded-lg ${
                          finding.status === 'missing' ? 'bg-red-50' : 
                          finding.status === 'partial' ? 'bg-amber-50' : 'bg-emerald-50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          finding.status === 'missing' ? 'bg-red-200' : 
                          finding.status === 'partial' ? 'bg-amber-200' : 'bg-emerald-200'
                        }`}>
                          {finding.status === 'missing' ? (
                            <AlertTriangle className="w-4 h-4 text-red-700" />
                          ) : finding.status === 'partial' ? (
                            <Clock className="w-4 h-4 text-amber-700" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-emerald-700" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-[#102A43]">{finding.area}</h3>
                          <p className="text-sm text-[#64748B] mt-1">{finding.finding}</p>
                          <p className="text-sm text-[#2F80ED] mt-2 font-medium">
                            → {finding.recommendation}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Priority Actions */}
              {actionPlan.priority_actions && actionPlan.priority_actions.length > 0 && (
                <div className="bg-white border border-[#E2E8F0] rounded-[12px] p-8 mb-8" data-testid="priority-actions-card">
                  <h2 className="text-xl font-bold text-[#102A43] mb-6 flex items-center gap-3">
                    <Zap className="w-6 h-6 text-amber-500" />
                    Your Next 3 Actions
                  </h2>
                  <div className="space-y-4">
                    {actionPlan.priority_actions.slice(0, 3).map((action, index) => (
                      <div key={index} data-testid={`priority-action-${index}`} className="flex items-start gap-4 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                        <div className="w-10 h-10 bg-[#2F80ED] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                          {action.priority || index + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-[#102A43]">{action.action}</h3>
                          <p className="text-sm text-[#64748B] mt-1">{action.why}</p>
                          {action.timeframe && (
                            <p className="text-xs text-[#94A3B8] mt-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {action.timeframe}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Product Recommendation */}
          <div className="bg-[#102A43] text-white rounded-[12px] p-8 mb-8">
            <div className="text-center mb-6">
              <p className="text-[#94A3B8] text-sm uppercase tracking-wide mb-2">Recommended Next Step</p>
              <h2 className="text-2xl font-bold mb-2">{recommendedProduct.name}</h2>
              <p className="text-[#CBD5E1] mb-4">{recommendedProduct.description}</p>
              {actionPlan?.recommended_product?.reason && (
                <p className="text-sm text-[#94A3B8] italic mb-4">
                  &ldquo;{actionPlan.recommended_product.reason}&rdquo;
                </p>
              )}
              <p className="text-4xl font-bold text-white mb-6">{recommendedProduct.price}</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {recommendedProduct.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-sm text-[#CBD5E1]">{feature}</span>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {recommendedProduct.link ? (
                <Link to={recommendedProduct.link}>
                  <Button 
                    className="w-full sm:w-auto bg-[#2F80ED] hover:bg-[#2563eb] text-white px-8 py-6 text-lg rounded-[8px]"
                    data-testid="recommended-product-cta"
                  >
                    {recommendedProduct.cta}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              ) : (
                <Button 
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="w-full sm:w-auto bg-[#2F80ED] hover:bg-[#2563eb] text-white px-8 py-6 text-lg rounded-[8px]"
                  data-testid="checkout-btn"
                >
                  {checkoutLoading ? 'Processing...' : recommendedProduct.cta}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
            
            {/* Checkout Error Banner - dismissible inline alert */}
            {checkoutError && (
              <div className="mt-4 p-4 bg-red-900/50 border border-red-500/50 rounded-lg flex items-center justify-between" data-testid="checkout-error-banner">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-200 text-sm">{checkoutError}</p>
                </div>
                <button 
                  onClick={() => setCheckoutError('')}
                  className="text-red-300 hover:text-white text-xl font-bold px-2"
                  aria-label="Dismiss error"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Axiom CTA */}
          <div className="bg-gradient-to-r from-[#2F80ED]/10 to-[#9333EA]/10 border border-[#2F80ED]/20 rounded-[12px] p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2F80ED] to-[#9333EA] rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-lg font-bold text-[#102A43] mb-1">Have Questions About Your Results?</h3>
                <p className="text-[#64748B] text-sm mb-3">
                  Ask Axiom, our AI governance assistant. It understands your score and can explain what each finding means.
                </p>
                <Button 
                  variant="outline" 
                  onClick={openAxiomWithContext}
                  className="border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED]/5"
                  data-testid="ask-axiom-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Ask Axiom About Your Score
                </Button>
              </div>
            </div>
          </div>

          {/* Secondary CTAs */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Link 
              to="/free-tools"
              data-testid="free-tools-link"
              className="bg-white border border-[#E2E8F0] rounded-[12px] p-6 hover:border-[#2F80ED] transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F8FAFC] rounded-full flex items-center justify-center group-hover:bg-[#2F80ED]/10">
                  <FileText className="w-6 h-6 text-[#64748B] group-hover:text-[#2F80ED]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#102A43]">Explore Free Tools</h3>
                  <p className="text-sm text-[#64748B]">Policy generator, inventory builder, and more</p>
                </div>
                <ChevronRight className="w-5 h-5 text-[#64748B] ml-auto" />
              </div>
            </Link>
            
            <Link 
              to="/articles"
              className="bg-white border border-[#E2E8F0] rounded-[12px] p-6 hover:border-[#2F80ED] transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F8FAFC] rounded-full flex items-center justify-center group-hover:bg-[#2F80ED]/10">
                  <Building2 className="w-6 h-6 text-[#64748B] group-hover:text-[#2F80ED]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#102A43]">AI Governance Resources</h3>
                  <p className="text-sm text-[#64748B]">165+ articles on AI accountability</p>
                </div>
                <ChevronRight className="w-5 h-5 text-[#64748B] ml-auto" />
              </div>
            </Link>
          </div>

          {/* Disclaimer */}
          <div className="text-center text-xs text-[#94A3B8] px-8">
            <p>
              AASC provides educational resources and tools for AI governance. We are not a regulator, 
              certifier, auditor, or legal authority. The accountability score is educational and does 
              not constitute legal advice or regulatory compliance assessment.
            </p>
          </div>
          
        </div>
      </main>
      
      <MainFooter />
      <LegalFooter />
    </div>
  );
}
